/**
 * Alshallal Pastry Company HR System - Request Timeline Component
 * Phase 24.2: Visual timeline showing request journey through approval chain
 * 
 * Shows: Submitted → Manager Reviewed → HR Reviewed → Complete/Rejected
 * Supports both Requests and Leave requests
 */
import React from 'react';
import { format } from 'date-fns';
import {
  Send,
  UserCheck,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

const TIMELINE_STEPS = {
  // Request timeline steps
  request: [
    { key: 'submitted', label: { en: 'Submitted', ar: 'تم التقديم' }, icon: Send },
    { key: 'manager', label: { en: 'Manager Review', ar: 'مراجعة المدير' }, icon: UserCheck },
    { key: 'hr', label: { en: 'HR Review', ar: 'مراجعة الموارد البشرية' }, icon: ShieldCheck },
    { key: 'complete', label: { en: 'Complete', ar: 'مكتمل' }, icon: CheckCircle2 },
  ],
  // Leave timeline steps (same structure)
  leave: [
    { key: 'submitted', label: { en: 'Submitted', ar: 'تم التقديم' }, icon: Send },
    { key: 'manager', label: { en: 'Manager Review', ar: 'مراجعة المدير' }, icon: UserCheck },
    { key: 'hr', label: { en: 'HR Review', ar: 'مراجعة الموارد البشرية' }, icon: ShieldCheck },
    { key: 'complete', label: { en: 'Complete', ar: 'مكتمل' }, icon: CheckCircle2 },
  ]
};

/**
 * Determine step status based on request data
 */
const getStepStatus = (stepKey, request) => {
  const status = request.status;
  const managerAction = request.manager_action;
  const hrAction = request.hr_action;
  
  // Submitted step is always complete
  if (stepKey === 'submitted') {
    return { status: 'complete', timestamp: request.submitted_at };
  }
  
  // Manager step
  if (stepKey === 'manager') {
    if (status === 'pending_manager') {
      return { status: 'current', timestamp: null };
    }
    if (managerAction === 'approve') {
      return { 
        status: 'complete', 
        timestamp: request.manager_action_at,
        approver: request.manager_name,
        action: 'approved',
        reason: request.manager_reason
      };
    }
    if (managerAction === 'reject' || request.rejected_by_role === 'manager') {
      return { 
        status: 'rejected', 
        timestamp: request.manager_action_at,
        approver: request.manager_name,
        action: 'rejected',
        reason: request.rejection_reason || request.manager_reason
      };
    }
    // If no manager assigned (went directly to HR)
    if (status === 'pending_hr' && !request.manager_id) {
      return { status: 'skipped', timestamp: null };
    }
    if (['pending_hr', 'approved', 'rejected'].includes(status) && !managerAction && !request.manager_id) {
      return { status: 'skipped', timestamp: null };
    }
    if (['pending_hr', 'approved'].includes(status)) {
      return { 
        status: 'complete', 
        timestamp: request.manager_action_at,
        approver: request.manager_name,
        action: 'approved'
      };
    }
    return { status: 'pending', timestamp: null };
  }
  
  // HR step
  if (stepKey === 'hr') {
    if (status === 'pending_hr') {
      return { status: 'current', timestamp: null };
    }
    if (hrAction === 'approve' || status === 'approved') {
      return { 
        status: 'complete', 
        timestamp: request.hr_action_at,
        approver: request.hr_action_by ? 'HR Manager' : null,
        action: 'approved',
        reason: request.hr_reason
      };
    }
    if (hrAction === 'reject' || (status === 'rejected' && request.rejected_by_role === 'hr')) {
      return { 
        status: 'rejected', 
        timestamp: request.hr_action_at,
        approver: 'HR Manager',
        action: 'rejected',
        reason: request.rejection_reason || request.hr_reason
      };
    }
    // If manager rejected, HR step doesn't happen
    if (status === 'rejected' && request.rejected_by_role === 'manager') {
      return { status: 'skipped', timestamp: null };
    }
    return { status: 'pending', timestamp: null };
  }
  
  // Complete step
  if (stepKey === 'complete') {
    if (status === 'approved') {
      return { status: 'complete', timestamp: request.hr_action_at || request.updated_at };
    }
    if (status === 'rejected') {
      return { status: 'rejected', timestamp: request.hr_action_at || request.manager_action_at };
    }
    return { status: 'pending', timestamp: null };
  }
  
  return { status: 'pending', timestamp: null };
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  try {
    return format(new Date(timestamp), 'MMM d, yyyy HH:mm');
  } catch {
    return timestamp;
  }
};

/**
 * Timeline Step Component
 */
const TimelineStep = ({ step, stepData, isLast, isRTL, language }) => {
  const Icon = step.icon;
  const label = language === 'ar' ? step.label.ar : step.label.en;
  
  const statusStyles = {
    complete: {
      iconBg: 'bg-green-500',
      iconColor: 'text-white',
      lineColor: 'bg-green-500',
      textColor: 'text-green-700'
    },
    current: {
      iconBg: 'bg-blue-500 animate-pulse',
      iconColor: 'text-white',
      lineColor: 'bg-slate-200',
      textColor: 'text-blue-700'
    },
    rejected: {
      iconBg: 'bg-red-500',
      iconColor: 'text-white',
      lineColor: 'bg-red-500',
      textColor: 'text-red-700'
    },
    skipped: {
      iconBg: 'bg-slate-300',
      iconColor: 'text-slate-500',
      lineColor: 'bg-slate-200',
      textColor: 'text-slate-400'
    },
    pending: {
      iconBg: 'bg-slate-200',
      iconColor: 'text-slate-400',
      lineColor: 'bg-slate-200',
      textColor: 'text-slate-400'
    }
  };
  
  const styles = statusStyles[stepData.status] || statusStyles.pending;
  
  return (
    <div className={cn("flex items-start gap-3", isRTL && "flex-row-reverse")}>
      {/* Icon */}
      <div className="flex flex-col items-center">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          styles.iconBg
        )}>
          {stepData.status === 'rejected' ? (
            <XCircle className={cn("h-5 w-5", styles.iconColor)} />
          ) : stepData.status === 'current' ? (
            <Clock className={cn("h-5 w-5", styles.iconColor)} />
          ) : (
            <Icon className={cn("h-5 w-5", styles.iconColor)} />
          )}
        </div>
        {/* Connector line */}
        {!isLast && (
          <div className={cn("w-0.5 h-12", styles.lineColor)} />
        )}
      </div>
      
      {/* Content */}
      <div className={cn("flex-1 pb-8", isLast && "pb-0")}>
        <div className={cn("font-medium", styles.textColor)}>
          {label}
          {stepData.status === 'skipped' && (
            <span className="text-xs font-normal ms-2">
              ({isRTL ? 'تم التخطي' : 'Skipped'})
            </span>
          )}
        </div>
        
        {/* Timestamp */}
        {stepData.timestamp && (
          <div className="text-sm text-slate-500 mt-0.5">
            {formatTimestamp(stepData.timestamp)}
          </div>
        )}
        
        {/* Approver info */}
        {stepData.approver && (
          <div className="text-sm text-slate-600 mt-1">
            <span className="font-medium">
              {stepData.action === 'approved' 
                ? (isRTL ? 'تمت الموافقة بواسطة: ' : 'Approved by: ')
                : (isRTL ? 'تم الرفض بواسطة: ' : 'Rejected by: ')
              }
            </span>
            {stepData.approver}
          </div>
        )}
        
        {/* Reason/Comments */}
        {stepData.reason && (
          <div className={cn(
            "text-sm mt-1 p-2 rounded",
            stepData.action === 'rejected' ? "bg-red-50 text-red-600" : "bg-slate-50 text-slate-600"
          )}>
            <span className="font-medium">
              {isRTL ? 'ملاحظات: ' : 'Comments: '}
            </span>
            {stepData.reason}
          </div>
        )}
        
        {/* Current status indicator */}
        {stepData.status === 'current' && (
          <div className="text-sm text-blue-600 mt-1 font-medium">
            {isRTL ? 'في انتظار المراجعة...' : 'Awaiting review...'}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Request Timeline Component
 * 
 * @param {object} request - The request object with approval data
 * @param {string} type - 'request' or 'leave'
 * @param {string} language - 'en' or 'ar'
 * @param {boolean} isRTL - RTL layout flag
 */
const RequestTimeline = ({ request, type = 'request', language = 'en', isRTL = false }) => {
  if (!request) return null;
  
  const steps = TIMELINE_STEPS[type] || TIMELINE_STEPS.request;
  
  return (
    <div className="py-2" data-testid="request-timeline">
      <h4 className={cn(
        "font-medium text-slate-900 mb-4 flex items-center gap-2",
        isRTL && "flex-row-reverse"
      )}>
        <ArrowRight className={cn("h-4 w-4", isRTL && "rotate-180")} />
        {isRTL ? 'مسار الطلب' : 'Request Journey'}
      </h4>
      
      <div className={cn(isRTL && "text-right")}>
        {steps.map((step, index) => {
          const stepData = getStepStatus(step.key, request);
          return (
            <TimelineStep
              key={step.key}
              step={step}
              stepData={stepData}
              isLast={index === steps.length - 1}
              isRTL={isRTL}
              language={language}
            />
          );
        })}
      </div>
    </div>
  );
};

export default RequestTimeline;
